import { HostAppUtil } from '../backend/office-js/HostAppUtil';
import { NamedError } from '../utils/Errors';

export class LoadTranscriptionStringsError extends NamedError<LoadTranscriptionStringsErrors> {}

export enum LoadTranscriptionStringsErrors {
    FailedToLoadStrings = 'FailedToLoadStrings',
    InvalidInput = 'InvalidInput'
}

export const LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE = "Hmmm...something went wrong. Please try again later.";
const DEFAULT_LOCALE = 'en-US';

// List of all language codes we support. These come from the directories in node_modules\@ms\office-online-strings.
const supportedLanguages = ['af-ZA', 'am-ET', 'ar-SA', 'as-IN', 'az-Latn-AZ', 'be-BY', 'bg-BG', 'bn-BD', 'bn-IN', 'bs-Latn-BA', 'ca-ES', 'ca-Es-VALENCIA', 'chr-Cher-US', 'cs-CZ', 'cy-GB', 'da-DK', 'de-DE', 'el-GR', 'en-GB', 'en-US', 'es-ES', 'es-MX', 'et-EE', 'eu-ES', 'fa-IR', 'fi-FI', 'fil-PH', 'fr-CA', 'fr-FR', 'ga-IE', 'gd-gb', 'gl-ES', 'gu-IN', 'ha-Latn-NG', 'he-IL', 'hi-IN', 'hr-HR', 'hu-HU', 'hy-AM', 'id-ID', 'is-IS', 'it-IT', 'ja-JP', 'ka-GE', 'kk-KZ', 'km-KH', 'kn-IN', 'ko-KR', 'kok-IN', 'ky-KG', 'lb-LU', 'lib', 'lo-LA', 'lt-LT', 'lv-LV', 'mi-NZ', 'mk-MK', 'ml-IN', 'mn-MN', 'mr-IN', 'ms-MY', 'mt-MT', 'nb-NO', 'ne-NP', 'nl-NL', 'nn-NO', 'or-IN', 'pa-IN', 'pl-PL', 'prs-AF', 'pt-BR', 'pt-PT', 'quz-PE', 'ro-RO', 'ru-RU', 'sd-Arab-PK', 'si-LK', 'sk-SK', 'sl-SI', 'sq-AL', 'sr-Cyrl-BA', 'sr-Cyrl-RS', 'sr-Latn-RS', 'sv-SE', 'sw-KE', 'ta-IN', 'te-IN', 'th-TH', 'tk-TM', 'tr-TR', 'tt-RU', 'ug-CN', 'uk-UA', 'ur-PK', 'uz-Latn-UZ', 'vi-VN', 'zh-CN', 'zh-TW'];

// This function is a last-resort we use to map an unknown language code to a default one.
// Default language codes are whatever language code for the required language we first find in the supportedLanguages array.
// This is a workaround while we get better language codes from WAC (VSO:4171604 tracks this work).
function GetDefaultLanguageCode(languageCode: string): string {
  const languageCodeParts = languageCode.split('-');

  for (let i = 0; i < supportedLanguages.length; i++) {
    const supportedLanguageCode = supportedLanguages[i];
    if (languageCodeParts[0] == supportedLanguageCode.split('-')[0])
      return supportedLanguageCode;
  }

  return languageCode;
}

// This function maps exotic language codes to language codes we support.
// Mapping is done manually so this can be thought of as "sensible, human-curated defaults" (e.g. en-CA is mapped to en-US instead of en-GB).
function GetSupportedLanguageCode(displayLanguage: string): string {
  switch (displayLanguage) {
    case 'es-US':
      return 'es-MX';
    case 'en':
      return 'en-US';
    case 'sv':
      return 'sv-SE';
    case 'it':
      return 'it-IT';
    case 'es-419':
      return 'es-MX';
    case 'en-CA':
      return 'en-US';
    case 'th':
      return 'th-TH';
    case 'de':
      return 'de-DE';
    case 'es-PE':
      return 'es-MX';
    default:
      return displayLanguage;
  }
}

function LoadScriptResourceAsync(scriptUrl: string, userLang: string): Promise<void> {
  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = scriptUrl;

  const loadPromise: Promise<void> = new Promise((resolve, reject) => {
    script.onload = () => {
      resolve();
    };

    script.onerror = script.onabort = () => {
      const displayLanguage = HostAppUtil.GetHostAppDisplayLanguage();
      reject(new LoadTranscriptionStringsError(LoadTranscriptionStringsErrors.FailedToLoadStrings, "Failed to load strings with mapped language = " + userLang + " and display language = " + displayLanguage));
   }
  });

  document.head.appendChild(script);
  return loadPromise;
}

export async function LoadTranscriptionStringsAsync(): Promise<void> {
  let userLang : string = '';

  const displayLanguage = HostAppUtil.GetHostAppDisplayLanguage();
  if (displayLanguage == null)
    throw new LoadTranscriptionStringsError(LoadTranscriptionStringsErrors.InvalidInput, " Display Language is undefined or null");

  //Formatting language code coming from display api to match standard locale format.
  const formattedDisplayLanguage = FormatLanguageCode(displayLanguage);

  // Try to map the user's language code to a known one.
  userLang = GetSupportedLanguageCode(formattedDisplayLanguage);

  if (supportedLanguages.indexOf(userLang) == -1) {
    // If we still don't have a supported language code, try to find one we support for the required language.
    userLang = GetDefaultLanguageCode(userLang);
  }

  if (supportedLanguages.indexOf(userLang) == -1) {
    // If we still don't have a supported language code, set default language en-US.
    userLang = DEFAULT_LOCALE;
  }

  const stringDictionaryJSName = 'transcription-strings.min.js';
  const stringHostPath = 'office-online-strings';
  const stringDictionaryJSUrl = stringHostPath + '/' + userLang + '/' + stringDictionaryJSName;

  try {
    return LoadScriptResourceAsync(stringDictionaryJSUrl, userLang);
  } catch (e) {
    const displayLanguage = HostAppUtil.GetHostAppDisplayLanguage();
    throw new LoadTranscriptionStringsError(LoadTranscriptionStringsErrors.FailedToLoadStrings, "Failed to load strings with mapped language = " + userLang + " and display language = " + displayLanguage);
  }
}

// This function formats any hyphen separated error code into Standard locale format.
function FormatLanguageCode(languageCode: string): string {
  const languageCodeParts = languageCode.split('-');

  //If length = 1, then returning with lowercase convention.
  if (languageCodeParts.length == 1) {
    return languageCodeParts[0].toLowerCase();
  }

  //Setting first part as smaller case only if length is > 1
  //and Setting last part as upper case only if length is > 1
  if (languageCodeParts.length > 1) {
    languageCodeParts[0] = languageCodeParts[0].toLowerCase();
    languageCodeParts[languageCodeParts.length -1] = languageCodeParts[languageCodeParts.length -1].toUpperCase();
  }

  //Capitalizing first letter of all parts except first and last
  if (languageCodeParts.length > 2) {
    for (let i = 1; i < languageCodeParts.length -1; i++) {
        languageCodeParts[i] = languageCodeParts[i].charAt(0).toUpperCase() + languageCodeParts[i].slice(1).toLowerCase();;
    }
  }

  return languageCodeParts.join('-');
}